"use client";

/**
 * ScrollWorld — autoplaying "fly through the world" hero (scroll-world skill port).
 *
 * This is the design language of oso95/scroll-world WITHOUT the Higgsfield /
 * video pipeline: a cinematic, self-contained hero that auto-advances through
 * book / brain-themed scenes, carries a live floating atmosphere (moving
 * characters), and is fully theme-aware (it reads the app's own CSS tokens so
 * the ThemeSwitch drives its palette). No external assets, no images.
 *
 * It occupies exactly one viewport (100dvh) and sits at the TOP of the page;
 * normal content simply follows. Autoplay_runs_on_a_timer, pauses on hover /
 * focus / tab-hidden, and honors prefers-reduced-motion.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { useTheme } from "next-themes";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  BrainCircuit,
  GraduationCap,
  Compass,
  Layers,
  Sparkles,
  Network,
  Mail,
  Globe,
  type LucideIcon,
} from "lucide-react";

const ICONS: Record<string, LucideIcon> = {
  book: BookOpen,
  brain: BrainCircuit,
  compas: Compass,
  layers: Layers,
  spark: Sparkles,
  network: Network,
};

export type ScrollWorldScene = {
  id: string;
  label: string;
  accent: string;
  icon: string;
  eyebrow?: string;
  title?: string;
  body?: string;
  tags?: string[];
  cta?: {
    primary?: { label: string; href: string };
    secondary?: { label: string; href: string };
  };
};

const DEFAULT_SCENES: ScrollWorldScene[] = [
  {
    id: "material",
    label: "Material",
    accent: "#E8B54D",
    icon: "book",
    eyebrow: "From notes to knowledge",
    title: "It starts with your material.",
    body: "Your lecture notes, past questions, and textbooks — the raw ground every answer grows from.",
    tags: [],
  },
  {
    id: "retrieval",
    label: "Retrieval",
    accent: "#5FB4A2",
    icon: "compass",
    eyebrow: "Curriculum-aligned retrieval",
    title: "We search your course, not the web.",
    body: "The AI reads your actual materials, so every answer is anchored to what you are truly taught.",
    tags: ["Source-cited", "On-syllabus"],
  },
  {
    id: "enrich",
    label: "Enrich",
    accent: "#8A7BB5",
    icon: "brain",
    eyebrow: "Pedagogical enrichment",
    title: "Every note, understood.",
    body: "Material is tagged with difficulty, common misconceptions, and exam relevance — automatically.",
    tags: ["Difficulty", "Misconceptions", "Exam-weighted"],
  },
  {
    id: "format",
    label: "Format",
    accent: "#E28F7B",
    icon: "layers",
    eyebrow: "Nigerian-professor format",
    title: "Answers your lecturer would recognise.",
    body: "A structured five-section format familiar to every Nigerian university student.",
    tags: ["5-section", "Structured"],
  },
  {
    id: "cascade",
    label: "Cascade",
    accent: "#6D93C9",
    icon: "spark",
    eyebrow: "Multi-provider AI cascade",
    title: "Always answering.",
    body: "Dozens of models cascade behind the scenes, so peak exam season never stops you.",
    tags: ["Resilient", "24/7"],
  },
  {
    id: "start",
    label: "Start",
    accent: "#E8B54D",
    icon: "brain",
    eyebrow: "Your companion",
    title: "Learn like the whole campus has your back.",
    body: "Built by students, for students — UniUI turns study material into precise, cited answers.",
    tags: [],
    cta: {
      primary: { label: "Get Started", href: "/auth/signup" },
      secondary: { label: "Explore Learn", href: "/learn" },
    },
  },
];

// Deterministic floating "characters" — book / brain / cap / spark glyphs that
// drift continuously so the scene feels alive (no hydration mismatch: same on
// server + client).
const FLOATERS = (() => {
  const kinds = ["book", "brain", "grad", "spark", "book", "brain"] as const;
  const seeds = [11, 29, 47, 63, 77, 88, 18, 34, 52, 66, 83, 95, 5, 23, 41, 58];
  return seeds.map((_, k) => {
    const dur = 14 + (seeds[(k * 7) % seeds.length] % 20);
    return {
      kind: kinds[k % kinds.length],
      left: seeds[k % seeds.length],
      top: (seeds[(k * 3) % seeds.length] * 1.27) % 100,
      scale: 0.6 + ((seeds[(k * 5) % seeds.length] % 50) / 50) * 0.9,
      dur,
      delay: -(seeds[(k * 2) % seeds.length] % dur),
    };
  });
})();

const FLOAT_ICONS: Record<string, LucideIcon> = {
  book: BookOpen,
  brain: BrainCircuit,
  grad: GraduationCap,
  spark: Sparkles,
};

const pad = (n: number) => String(n).padStart(2, "0");

export default function ScrollWorld({
  scenes = DEFAULT_SCENES,
  autoplayMs = 5200,
}: {
  scenes?: ScrollWorldScene[];
  autoplayMs?: number;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== "light";
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const pauseRef = useRef(false);
  const indexRef = useRef(0);
  const N = scenes.length;

  const go = useCallback(
    (i: number) => {
      const next = ((i % N) + N) % N;
      indexRef.current = next;
      setActive(next);
    },
    [N],
  );

  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Autoplay timer (skipped under reduced motion).
  useEffect(() => {
    if (reduce || autoplayMs <= 0 || N <= 1) return;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timer = setTimeout(() => {
        if (!pauseRef.current) go(indexRef.current + 1);
        schedule();
      }, autoplayMs);
    };
    schedule();
    return () => clearTimeout(timer);
  }, [go, autoplayMs, reduce, N]);

  const scene = scenes[active];
  const SceneIcon = scene ? (ICONS[scene.icon] ?? BookOpen) : BookOpen;

  return (
    <div
      ref={rootRef}
      className="sw-hero relative w-full overflow-hidden isolate"
      style={
        {
          height: "100dvh",
          ["--sw-accent" as string]: scene?.accent ?? DEFAULT_SCENES[0].accent,
        } as React.CSSProperties
      }
      onMouseEnter={() => {
        pauseRef.current = true;
        setPaused(true);
      }}
      onMouseLeave={() => {
        pauseRef.current = false;
        setPaused(false);
      }}
      onFocusCapture={() => {
        pauseRef.current = true;
        setPaused(true);
      }}
      onBlurCapture={() => {
        pauseRef.current = false;
        setPaused(false);
      }}
    >
      {/* -- Stage -- */}
      <div className="sw-stage">
        <div className="sw-grid">
          {/* Left: copy */}
          <div className="sw-copywrap">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={scene?.id ?? active}
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -22 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="sw-copy"
              >
                <span className="sw-copy__num">
                  {pad(active + 1)} / {pad(N)}
                </span>
                {scene?.eyebrow && (
                  <span className="sw-copy__eyebrow">{scene.eyebrow}</span>
                )}
                {scene?.title && (
                  <h2 className="sw-copy__title">{scene.title}</h2>
                )}
                {scene?.body && <p className="sw-copy__body">{scene.body}</p>}
                {scene?.tags && scene.tags.length > 0 && (
                  <ul className="sw-copy__tags">
                    {scene.tags.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                )}
                {scene?.cta && (
                  <div className="sw-copy__cta">
                    {scene.cta.primary && (
                      <a
                        className="sw-btn sw-btn--primary"
                        href={scene.cta.primary.href}
                      >
                        {scene.cta.primary.label}
                      </a>
                    )}
                    {scene.cta.secondary && (
                      <a
                        className="sw-btn sw-btn--ghost"
                        href={scene.cta.secondary.href}
                      >
                        {scene.cta.secondary.label}
                      </a>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right: book / brain "scene" illustration */}
          <div className="sw-scenewrap" aria-hidden>
            <AnimatePresence mode="wait">
              <motion.div
                key={scene?.id ?? active}
                initial={{ opacity: 0, scale: 0.86, rotate: -6 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 1.08, rotate: 6 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="sw-scene-orb"
              >
                <div className="sw-scene-orb__ring" />
                <div className="sw-scene-orb__core">
                  <SceneIcon size={84} strokeWidth={1.3} />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* -- Route rail -- */}
      <div className="sw-route">
        {scenes.map((s, i) => (
          <button
            key={s.id}
            onClick={() => go(i)}
            className={`sw-route dot ${i === active ? "is-active" : ""}`}
            style={
              { ["--sw-accent" as string]: s.accent } as React.CSSProperties
            }
            aria-label={`Go to ${s.label}`}
            aria-current={i === active}
          >
            <span className="sw-route__label">{s.label}</span>
            <i />
          </button>
        ))}
      </div>

      {/* -- Autoplay progress + hint -- */}
      <div className="sw-foot">
        <div className="sw-progress" aria-hidden>
          <span
            key={active}
            className={`sw-progress fill ${paused ? "is-paused" : ""}`}
            style={
              {
                animationDuration: `${autoplayMs}ms`,
                background: scene?.accent,
              } as React.CSSProperties
            }
          />
        </div>
        <button
          className="sw-autoplay"
          onClick={() => {
            pauseRef.current = !pauseRef.current;
            setPaused(pauseRef.current);
          }}
          aria-pressed={paused}
        >
          {paused ? "▶ Play" : "❚❚ Pause"}
        </button>
      </div>

      <style dangerouslySetInnerHTML={{ __html: CSS }} />
    </div>
  );
}

const CSS = `
.sw-hero{
  --sw-bg:var(--background);
  --sw-ink:var(--foreground);
  --sw-ink-soft:var(--muted-foreground);
  --sw-card:var(--card);
  --sw-border:var(--border);
  color:var(--sw-ink);
  font-family:var(--font-syne,system-ui,sans-serif);
  border-radius:0 0 2rem 2rem;
  display:flex;flex-direction:column;
}
.sw-sky{position:absolute;inset:0;z-index:0;overflow:hidden;pointer-events:none;}
.sw-sky__grad{position:absolute;inset:-12%;background:linear-gradient(178deg,color-mix(in srgb,var(--sw-accent) 16%,var(--sw-bg)) 0%,var(--sw-bg) 58%,color-mix(in srgb,var(--sw-accent) 8%,var(--sw-bg)) 100%);transition:background .7s;}
.sw-sky__glow{position:absolute;inset:0;background:radial-gradient(58% 44% at 76% 18%,color-mix(in srgb,var(--sw-accent) 30%,transparent),transparent 70%),radial-gradient(46% 36% at 26% 64%,color-mix(in srgb,var(--sw-accent) 16%,transparent),transparent 70%);transition:background .7s;}
.sw-floaters{position:absolute;inset:0;will-change:transform;}
.sw-floater{position:absolute;color:color-mix(in srgb,var(--sw-accent) 55%,var(--sw-ink-soft));opacity:.5;transform:scale(var(--fs,1));animation:sw-float linear infinite;filter:drop-shadow(0 4px 18px color-mix(in srgb,var(--sw-accent) 30%,transparent));}
@keyframes sw-float{0%{opacity:0;transform:scale(var(--fs,1)) translate(0,14vh) rotate(0)}12%{opacity:.55}88%{opacity:.45}100%{opacity:0;transform:scale(var(--fs,1)) translate(5vw,-24vh) rotate(200deg)}}

.sw-topbar{position:relative;z-index:30;display:flex;align-items:center;justify-content:space-between;gap:16px;padding:clamp(14px,2.2vw,24px) clamp(18px,5vw,56px);}
.sw-brand{display:flex;align-items:center;gap:10px;}
.sw-brand__name{font-weight:700;font-size:1.05rem;letter-spacing:-.01em;color:var(--sw-ink);}

.sw-stage{position:relative;z-index:10;flex:1;display:flex;align-items:center;}
.sw-grid{max-width:1200px;margin:0 auto;width:100%;padding:0 clamp(18px,5vw,56px);display:grid;grid-template-columns:1.1fr .9fr;gap:32px;align-items:center;}
.sw-copywrap{min-height:280px;display:flex;align-items:center;}
.sw-copy{max-width:520px;}
.sw-copy__num{font-family:ui-monospace,Menlo,monospace;font-size:.74rem;letter-spacing:.12em;color:var(--sw-ink-soft);}
.sw-copy__eyebrow{display:block;margin-top:16px;font-weight:700;font-size:.78rem;letter-spacing:.16em;text-transform:uppercase;color:var(--sw-accent);}
.sw-copy__title{font-family:var(--font-display,system-ui,sans-serif);font-weight:800;color:var(--sw-ink);font-size:clamp(1.9rem,4vw,3.2rem);line-height:1.04;margin:12px 0 0;letter-spacing:-.02em;}
.sw-copy__body{margin-top:16px;font-size:clamp(1rem,1.2vw,1.12rem);line-height:1.55;color:color-mix(in srgb,var(--sw-ink) 82%,var(--sw-ink-soft));max-width:42ch;}
.sw-copy__tags{list-style:none;display:flex;flex-wrap:wrap;gap:8px;margin:22px 0 0;padding:0;}
.sw-copy__tags li{font-size:.8rem;font-weight:600;color:color-mix(in srgb,var(--sw-accent) 75%,var(--sw-ink));padding:7px 14px;border-radius:999px;background:color-mix(in srgb,var(--sw-accent) 14%,transparent);border:1px solid color-mix(in srgb,var(--sw-accent) 32%,transparent);}
.sw-copy__cta{display:flex;flex-wrap:wrap;gap:12px;margin-top:26px;}
.sw-btn{text-decoration:none;font-weight:700;font-size:.85rem;padding:13px 24px;border-radius:999px;transition:transform .2s,filter .2s;text-transform:uppercase;letter-spacing:.05em;}
.sw-btn--primary{color:color-mix(in srgb,var(--sw-accent) 22%,#000);background:var(--sw-accent);} .sw-btn--primary:hover{transform:translateY(-2px);filter:brightness(1.06);}
.sw-btn--ghost{color:var(--sw-ink);border:1.5px_solid color-mix(in srgb,var(--sw-ink) 26%,transparent);} .sw-btn--ghost:hover{transform:translateY(-2px);}

.sw-scenewrap{display:flex;align-items:center;justify-content:center;min-height:340px;}
.sw-scene-orb{position:relative;width:min(38vh,320px);height:min(38vh,320px);display:grid;place-items:center;filter:drop-shadow(0 30px 80px color-mix(in srgb,var(--sw-accent) 35%,transparent));}
.sw-scene-orb__ring{position:absolute;inset:0;border-radius:46% 54% 52% 48%;border:2px solid color-mix(in srgb,var(--sw-accent) 45%,transparent);background:radial-gradient(circle at 38% 32%,color-mix(in srgb,var(--sw-accent) 30%,transparent),transparent 70%);animation:sw-spin 22s linear infinite;}
.sw-scene-orb__core{position:relative;width:62%;height:62%;border-radius:42% 58% 46% 54%;display:grid;place-items:center;color:color-mix(in srgb,var(--sw-accent) 25%,#000);background:linear-gradient(155deg,var(--sw-accent),color-mix(in srgb,var(--sw-accent) 55%,#000));box-shadow:inset 0 2px 18px color-mix(in srgb,#fff 28%,transparent);}
@keyframes sw-spin{to{transform:rotate(360deg)}}

.sw-route{position:absolute;right:clamp(12px,2.2vw,26px);top:50%;z-index:30;transform:translateY(-50%);display:flex;flex-direction:column;gap:18px;padding:16px 10px;}
.sw-route::before{content:"";position:absolute;left:50%;top:20px;bottom:20px;width:2px;transform:translateX(-50%);background:var(--sw-accent);opacity:.26;}
.sw-route__dot{position:relative;border:0;background:transparent;cursor:pointer;width:14px;height:14px;display:grid;place-items:center;}
.sw-route__dot i{width:9px;height:9px;border-radius:50%;background:color-mix(in srgb,var(--sw-accent) 42%,transparent);transition:transform .3s,background .3s,box-shadow .3s;}
.sw-route__dot:hover i{transform:scale(1.25);background:var(--sw-accent);}
.sw-route__dot.is-active i{background:var(--sw-accent);transform:scale(1.4);box-shadow:0 0 0 5px color-mix(in srgb,var(--sw-accent) 24%,transparent);}
.sw-route__label{position:absolute;right:22px;top:50%;transform:translateY(-50%) translateX(6px);white-space:nowrap;font-size:.76rem;font-weight:600;color:var(--sw-ink);background:color-mix(in srgb,var(--sw-card) 88%,transparent);backdrop-filter:blur(6px);padding:5px 11px;border-radius:999px;opacity:0;pointer-events:none;transition:opacity .25s,transform .25s;border:1px solid color-mix(in srgb,var(--sw-accent) 18%,transparent);}
.sw-route__dot:hover .sw-route__label,.sw-route__dot.is-active .sw-route__label{opacity:1;transform:translateY(-50%) translateX(0);}

.sw-foot{position:relative;z-index:30;display:flex;align-items:center;gap:16px;padding:0 clamp(18px,5vw,56px) clamp(16px,2.4vw,26px);}
.sw-progress{flex:1;height:3px;border-radius:999px;background:color-mix(in srgb,var(--sw-accent) 14%,transparent);overflow:hidden;}
.sw-progress__fill{display:block;height:100%;width:100%;transform-origin:0 50%;transform:scaleX(0);animation:sw-fill linear forwards;}
.sw-progress__fill.is-paused{animation-play-state:paused;}
@keyframes sw-fill{from{transform:scaleX(0)}to{transform:scaleX(1)}}
.sw-autoplay{font:inherit;font-size:.74rem;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:var(--sw-ink-soft);background:color-mix(in srgb,var(--sw-card) 70%,transparent);border:1px solid var(--sw-border);border-radius:999px;padding:7px 16px;cursor:pointer;transition:color .2s,border-color .2s;}
.sw-autoplay:hover{color:var(--sw-accent);border-color:color-mix(in srgb,var(--sw-accent) 40%,transparent);}

@media (max-width:900px){
  .sw-grid{grid-template-columns:1fr;gap:8px;text-align:center;}
  .sw-copywrap{justify-content:center;min-height:auto;}
  .sw-copy{margin:0 auto;}
  .sw-scenewrap{display:none;}
  .sw-route{gap:14px;right:6px;} .sw-route__label{display:none;}
  .sw-route__dot{width:24px;height:24px;}
}
@media (prefers-reduced-motion:reduce){
  .sw-floater,.sw-scene-orb__ring,.sw-progress__fill{animation:none!important;}
  .sw-progress__fill{transform:scaleX(1);}
}`;
